import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, Plus, Search, SlidersHorizontal, X } from 'lucide-react';

import { useUiStore } from '@/store/ui';
import { useSuche } from '@/modules/suche/sucheStore';
import { useSeitenleiste } from '@/app/layout/useSeitenleiste';
import { SeitenleistenNav } from '@/app/layout/SeitenleistenNav';
import { setSchmal } from '@/domain/oberflaeche/seitenleiste';
import { cn } from '@/lib/utils/cn';
import { de } from '@/i18n/de';

/**
 * Seitenleiste nach dem freigegebenen Entwurf (N-01, D-00). Der Inhalt - Gruppen,
 * Bereiche, Einstellungen - steht in `SeitenleistenNav`; hier nur die Huelle:
 * Kopf, Notiz, Suche und die Schalter "Anpassen" und "Nur Symbole".
 */
export function Sidebar() {
  const open = useUiStore((s) => s.sidebarOpen);
  const setOpen = useUiStore((s) => s.setSidebarOpen);
  const sucheOeffnen = useSuche((s) => s.oeffnen);
  const { einstellung, aendern } = useSeitenleiste();
  const [anpassen, setAnpassen] = useState(false);
  // "Nur Symbole" gilt am Laptop; auf dem Handy ist die Leiste ein Panel mit vollen Namen.
  const schmal = einstellung.schmal === true;
  const text = schmal ? 'md:hidden' : '';

  return (
    <>
      {/* Abdunklung nur auf schmalen Displays, wo die Leiste ueberlagert. */}
      {open && (
        <div
          aria-hidden="true"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-60 shrink-0 flex-col border-r border-line',
          'bg-sidebar transition-transform md:static md:translate-x-0',
          schmal && 'md:w-14',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <span className={cn('text-sm font-semibold tracking-tight', text)}>{de.app.name}</span>
          <button
            type="button"
            aria-label={de.common.close}
            onClick={() => setOpen(false)}
            className="grid size-8 place-items-center rounded-md text-ink-faint hover:text-ink md:hidden"
          >
            <X className="size-4" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-2">
          {/* Von jeder Seite aus eine Notiz anlegen (V-12, Vorschlag; Begriff seit F-48). */}
          <Link
            to="/ressourcen?neu=1"
            onClick={() => setOpen(false)}
            title={de.resources.neu}
            className="mb-2 flex items-center gap-2.5 rounded-md border border-accent/40 bg-accent/10 px-2.5 py-2 text-sm font-medium text-ink transition-colors hover:bg-accent/20"
          >
            <Plus className="size-4 shrink-0 text-accent-text" /> <span className={text}>{de.resources.neu}</span>
          </Link>
          {/* Globale Suche (F-18) - auch mit Strg+K. */}
          <button
            type="button"
            title="Suchen"
            onClick={() => { setOpen(false); sucheOeffnen(); }}
            className="mb-1 flex items-center gap-2.5 rounded-md border border-line px-2.5 py-2 text-left text-sm text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink"
          >
            <Search className="size-4 shrink-0" />
            <span className={cn('flex-1', text)}>Suchen</span>
            <kbd className={cn('rounded border border-line px-1 text-[10px] text-ink-faint', text)}>Strg K</kbd>
          </button>

          <SeitenleistenNav einstellung={einstellung} aendern={aendern} anpassen={anpassen && !schmal} schmal={schmal} schliessen={() => setOpen(false)} />
        </nav>

        <div className="flex items-center gap-1 border-t border-line px-2 py-2">
          {!schmal && (
            <button type="button" aria-pressed={anpassen} onClick={() => setAnpassen(!anpassen)}
              className={cn('flex flex-1 items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                anpassen ? 'bg-surface-raised text-ink' : 'text-ink-faint hover:bg-surface-raised hover:text-ink')}>
              <SlidersHorizontal className="size-4 shrink-0" /> {anpassen ? 'Fertig' : 'Seitenleiste anpassen'}
            </button>
          )}
          <button type="button" onClick={() => void aendern((e) => setSchmal(e, !schmal))}
            aria-label={schmal ? 'Seitenleiste ausklappen' : 'Nur Symbole zeigen'} title={schmal ? 'Seitenleiste ausklappen' : 'Nur Symbole zeigen'}
            className="hidden size-8 shrink-0 place-items-center rounded-md text-ink-faint hover:bg-surface-raised hover:text-ink md:grid">
            {schmal ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        </div>
      </aside>
    </>
  );
}
